/**
 * GstModeMixedModal — Sprint Apr-30 2026
 *
 * Shown when the backend returns { error: 'mixed_gst_mode' } (HTTP 422)
 * on proposal send / SO confirm. Offers three resolution paths:
 *
 *   (A) "Convert all lines to [doc mode]"
 *       → re-submits with ?forceMode=docMode  (backend coerces all lines)
 *
 *   (B) "Switch document to [other mode]"
 *       → sets docGstMode state in the editor to the opposite value and closes
 *         the modal so the user sees the toggle flip (no API call yet — the
 *         next save will succeed if all lines already have the non-doc mode).
 *
 *   (C) "Split into two proposals"
 *       → calls POST /api/v1/proposals/:id/split-gst, then navigates to /proposals
 *         with a success toast.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, GitFork, RefreshCw, ToggleLeft, X, AlertTriangle } from 'lucide-react';
import { api } from '@/utils/api';

export interface GstMixedModalProps {
  proposalId: string;
  documentMode: 'extra' | 'inclusive';
  inclusiveCount: number;
  extraCount: number;
  recommendation?: 'convert_all_to_doc_mode' | 'switch_doc_mode' | 'split';
  onConvertAll: () => void;      // caller re-sends with forceMode=docMode
  onSwitchDocMode: () => void;   // caller flips the local gstMode toggle
  onClose: () => void;
  onSplitSuccess?: () => void;   // optional: custom handler after split
}

export function GstModeMixedModal({
  proposalId,
  documentMode,
  inclusiveCount,
  extraCount,
  recommendation,
  onConvertAll,
  onSwitchDocMode,
  onClose,
  onSplitSuccess,
}: GstMixedModalProps) {
  const navigate = useNavigate();
  const [splitting, setSplitting] = useState(false);
  const [splitError, setSplitError] = useState<string | null>(null);

  const otherMode = documentMode === 'extra' ? 'inclusive' : 'extra';
  const docModeLabel = documentMode === 'extra' ? 'Extra (+ GST)' : 'Inclusive (GST incl.)';
  const otherModeLabel = otherMode === 'extra' ? 'Extra (+ GST)' : 'Inclusive (GST incl.)';

  const handleSplit = async () => {
    setSplitting(true);
    setSplitError(null);
    try {
      await api.post(`/proposals/${proposalId}/split-gst`);
      if (onSplitSuccess) {
        onSplitSuccess();
      } else {
        navigate('/proposals', {
          state: {
            toast: {
              type: 'success',
              message: 'Proposal split into two — one per GST mode. Original marked as split.',
            },
          },
        });
      }
    } catch (err: any) {
      setSplitError(err?.response?.data?.error ?? 'Split failed. Please try again.');
    } finally {
      setSplitting(false);
    }
  };

  return (
    /* Backdrop */
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-[var(--line,#e5e7eb)] px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-50">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-[var(--fg-1,#111827)]">Mixed GST Modes</h2>
              <p className="text-xs text-[var(--fg-3,#6b7280)]">This document has lines with different GST modes</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 rounded-full p-1 text-[var(--fg-3,#6b7280)] hover:bg-[var(--ivory-2,#f3f4f6)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* Stats chips */}
          <div className="mb-4 flex gap-3">
            <div className="flex-1 rounded-lg bg-blue-50 px-3 py-2 text-center">
              <div className="text-xl font-bold text-blue-700">{inclusiveCount}</div>
              <div className="text-xs text-blue-600">Inclusive lines</div>
            </div>
            <div className="flex-1 rounded-lg bg-purple-50 px-3 py-2 text-center">
              <div className="text-xl font-bold text-purple-700">{extraCount}</div>
              <div className="text-xs text-purple-600">Extra (+ GST) lines</div>
            </div>
            <div className="flex-1 rounded-lg bg-amber-50 px-3 py-2 text-center">
              <div className="text-xl font-bold text-amber-700">{documentMode.toUpperCase()}</div>
              <div className="text-xs text-amber-600">Document mode</div>
            </div>
          </div>

          <p className="mb-4 text-sm text-[var(--fg-2,#374151)]">
            The document is set to <strong>{docModeLabel}</strong> but contains lines with
            different modes. Choose how to resolve:
          </p>

          {/* Option A: Convert all */}
          <button
            onClick={onConvertAll}
            className={`mb-2 flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors hover:bg-[var(--ivory-1,#fafaf9)] ${
              recommendation === 'convert_all_to_doc_mode'
                ? 'border-[var(--saffron,#f59e0b)] ring-1 ring-[var(--saffron,#f59e0b)]'
                : 'border-[var(--line,#e5e7eb)]'
            }`}
          >
            <RefreshCw className="h-5 w-5 shrink-0 text-[var(--saffron,#f59e0b)]" />
            <div>
              <div className="text-sm font-medium text-[var(--fg-1,#111827)]">
                Convert all lines to {docModeLabel}
                {recommendation === 'convert_all_to_doc_mode' && (
                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">Recommended</span>
                )}
              </div>
              <div className="text-xs text-[var(--fg-3,#6b7280)]">
                All line items will be updated to match the document GST mode. Simplest option.
              </div>
            </div>
          </button>

          {/* Option B: Switch doc mode */}
          <button
            onClick={onSwitchDocMode}
            className={`mb-2 flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors hover:bg-[var(--ivory-1,#fafaf9)] ${
              recommendation === 'switch_doc_mode'
                ? 'border-[var(--saffron,#f59e0b)] ring-1 ring-[var(--saffron,#f59e0b)]'
                : 'border-[var(--line,#e5e7eb)]'
            }`}
          >
            <ToggleLeft className="h-5 w-5 shrink-0 text-blue-500" />
            <div>
              <div className="text-sm font-medium text-[var(--fg-1,#111827)]">
                Switch document to {otherModeLabel}
                {recommendation === 'switch_doc_mode' && (
                  <span className="ml-2 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">Recommended</span>
                )}
              </div>
              <div className="text-xs text-[var(--fg-3,#6b7280)]">
                Flip the document GST mode toggle to match the lines. Review before resending.
              </div>
            </div>
          </button>

          {/* Option C: Split */}
          <button
            onClick={handleSplit}
            disabled={splitting}
            className={`flex w-full items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors hover:bg-[var(--ivory-1,#fafaf9)] disabled:opacity-60 ${
              recommendation === 'split'
                ? 'border-[var(--saffron,#f59e0b)] ring-1 ring-[var(--saffron,#f59e0b)]'
                : 'border-[var(--line,#e5e7eb)]'
            }`}
          >
            {splitting ? (
              <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[var(--fg-3,#6b7280)]" />
            ) : (
              <GitFork className="h-5 w-5 shrink-0 text-emerald-500" />
            )}
            <div>
              <div className="text-sm font-medium text-[var(--fg-1,#111827)]">
                Split into two proposals — one per mode
                {recommendation === 'split' && (
                  <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700">Recommended</span>
                )}
              </div>
              <div className="text-xs text-[var(--fg-3,#6b7280)]">
                Creates two new draft proposals ({inclusiveCount} inclusive line{inclusiveCount !== 1 ? 's' : ''} +{' '}
                {extraCount} extra line{extraCount !== 1 ? 's' : ''}). Original is preserved as "split".
              </div>
            </div>
          </button>

          {splitError && (
            <p className="mt-2 text-xs text-red-600">{splitError}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-[var(--line,#e5e7eb)] px-6 py-3">
          <button
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm text-[var(--fg-2,#374151)] hover:bg-[var(--ivory-2,#f3f4f6)]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
